import { TrashIcon, UploadSimpleIcon } from '@phosphor-icons/react';
import { useRef, useState, type ChangeEvent, type JSX } from 'react';

import {
  ALLOWED_MIMES,
  formatBytes,
  validateStagedFile,
} from '@/components/logsheets-maker/stagedFileValidation';
import { Button } from '@/components/ui/button';

/**
 * Inline file-staging picker for the maker create + bulk wizards.
 *
 * Controlled component: parent owns the `files` array and is notified via
 * `onChange`. The picker itself does NOT upload anything — it just collects
 * `File` objects in browser memory. The wizard's submit handler is
 * responsible for firing the upload requests once the entry id(s) exist.
 *
 * Validation (MIME allowlist + 10 MiB cap) is performed client-side before a
 * file enters the staged list so the user sees errors inline without having
 * to wait for create+upload+fail. Backend re-validates either way.
 *
 * Use the existing DocumentUploadZone for the after-save flow on the entry
 * Edit page — that component talks directly to the per-entry upload endpoint
 * and supports remove + download for already-persisted documents. This one
 * is strictly pre-save staging.
 */

export interface StagedFilesPickerProps {
  readonly files: readonly File[];
  readonly onChange: (next: File[]) => void;
  readonly disabled?: boolean;
  /** Stable id used for the hidden <input>/label pair. Default 'staged'. */
  readonly idPrefix?: string;
  /** Optional test-id prefix so multiple instances on one page (bulk wizard)
   *  are independently addressable. Defaults to 'staged-files-picker'. */
  readonly testIdPrefix?: string;
}

export function StagedFilesPicker({
  files,
  onChange,
  disabled = false,
  idPrefix = 'staged',
  testIdPrefix = 'staged-files-picker',
}: StagedFilesPickerProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  function addFiles(picked: FileList | null): void {
    setError(null);
    if (picked === null || picked.length === 0) return;
    const incoming = Array.from(picked);
    for (const file of incoming) {
      const err = validateStagedFile(file);
      if (err !== null) {
        setError(err);
        return;
      }
    }
    // Append; the parent owns dedup if it cares (we don't here — the same
    // file picked twice produces two upload calls, server keeps both with
    // unique storage paths).
    onChange([...files, ...incoming]);
  }

  function removeAt(index: number): void {
    setError(null);
    const next = files.slice();
    next.splice(index, 1);
    onChange(next);
  }

  function onPicked(e: ChangeEvent<HTMLInputElement>): void {
    addFiles(e.target.files);
    if (inputRef.current !== null) {
      inputRef.current.value = '';
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    if (disabled) return;
    addFiles(e.dataTransfer.files);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>): void {
    if (disabled) return;
    e.preventDefault();
  }

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        className={
          'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-input p-4 text-center text-xs' +
          (disabled ? ' opacity-60' : '')
        }
      >
        <UploadSimpleIcon size={20} weight="duotone" />
        <p className="text-muted-foreground">Drag a file here or</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            inputRef.current?.click();
          }}
          data-testid={`${testIdPrefix}-button`}
          disabled={disabled}
        >
          Choose file
        </Button>
        <p className="text-xs text-muted-foreground">PDF, JPEG, or PNG up to 10 MB.</p>
        <input
          ref={inputRef}
          id={`${idPrefix}-input`}
          type="file"
          accept={ALLOWED_MIMES.join(',')}
          multiple
          className="hidden"
          onChange={onPicked}
          data-testid={`${testIdPrefix}-input`}
        />
      </div>

      {error !== null && (
        <p className="text-xs text-destructive" data-testid={`${testIdPrefix}-error`}>
          {error}
        </p>
      )}

      {files.length > 0 && (
        <ul className="space-y-1" data-testid={`${testIdPrefix}-list`}>
          {files.map((file, idx) => (
            <li
              key={`${file.name}-${String(file.size)}-${String(idx)}`}
              className="flex items-center justify-between gap-2 rounded-md border border-input bg-muted/30 px-3 py-2 text-xs"
              data-testid={`${testIdPrefix}-row-${String(idx)}`}
            >
              <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {formatBytes(file.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  removeAt(idx);
                }}
                disabled={disabled}
                aria-label={`Remove ${file.name}`}
                data-testid={`${testIdPrefix}-remove-${String(idx)}`}
              >
                <TrashIcon size={14} weight="bold" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
